/**
 * A casca: navegação e quem está logado.
 *
 * A versão anterior não tinha navegação de verdade — dois botões que
 * apareciam e sumiam conforme a "tela" ativa, sem URL. Voltar do navegador
 * não funcionava e não havia como mandar um link para alguém.
 */
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/cliente";
import { DEV_ATIVO } from "../dev";
import { useSessao } from "../sessao";
import { useTema } from "../tema";
import { ProvedorAgua, useAgua } from "./Agua";
import css from "./Moldura.module.css";

const ativoSe = ({ isActive }: { isActive: boolean }) => (isActive ? css.ativo : "");

/* AS CINCO TELAS DA ÁREA DE CONFIGURAÇÃO, na ordem da navegação lateral dela
   (`AreaConfig.tsx`). Na barra elas são UM link (20/10, "já que todas as
   configurações já estão aqui na esquerda, não é necessário um dropdown"):
   a gaveta repetia a coluna da esquerda. A lista fica para o link saber
   quando está ativo e para o botão do menu do celular dizer o nome da tela. */
const CONFIGURACAO = [
  { para: "/configuracao", rotulo: "Regras e ajustes" },
  { para: "/acervo", rotulo: "Base de licitações" },
  { para: "/fila", rotulo: "Funil de seleção" },
  { para: "/vocabulario", rotulo: "Palavras de busca" },
  { para: "/extracao", rotulo: "Buscas nos portais" },
];

/** Para onde o Voltar do celular leva quando NAO ha passo anterior dentro do
 * app — o link direto, a aba nova, o PDF aberto do WhatsApp: a tela-mae, e
 * nao para fora do sistema. */
function telaMae(pathname: string) {
  if (pathname.startsWith("/licitacao/")) return "/oportunidades";
  if (pathname.startsWith("/atestados/")) return "/atestados";
  return "/";
}

export function Moldura() {
  return (
    <ProvedorAgua>
      <Casca />
    </ProvedorAgua>
  );
}

function Casca() {
  const { usuario, sair } = useSessao();
  const { pathname } = useLocation();
  // A água é identidade na home e na varredura — ambas querem os reflexos
  // claros e o movimento vivos, sem nada escurecendo por cima — e ruído nas
  // telas de leitura: o gradiente tem uma faixa de luz forte que passa por
  // cima de tabela e cartão. Fora dessas duas telas ela vira fundo, não
  // cenário.
  const naVarredura = pathname.startsWith("/varredura");
  const semVeu = pathname === "/" || naVarredura;
  const { mergulhando } = useAgua();
  const [tema, defineTema] = useTema();
  // O TEMA NA RAIZ. A home segue o tema (19/10): no claro a água vira "água
  // rasa" (ver `Agua.tsx`). A VARREDURA é sempre o abismo — é a tela imersiva
  // do sistema, e o mergulho chega nela pelo escuro —, então ali o tema não
  // se aplica, e o botão não aparece (ele não teria efeito nenhum).
  const temaDaTela = naVarredura ? "escuro" : tema;
  useEffect(() => {
    document.documentElement.dataset.tema = temaDaTela;
    return () => {
      delete document.documentElement.dataset.tema;
    };
  }, [temaDaTela]);
  const claro = tema === "claro";
  const naLicitacao = pathname.startsWith("/licitacao/");
  const naConfiguracao = CONFIGURACAO.some(
    (r) => pathname === r.para || pathname.startsWith(r.para + "/"),
  );
  // As telas das abas são a raiz da navegação: ali não há "voltar" a oferecer,
  // a barra de abas é o caminho. A seta só aparece nas telas de dentro.
  const naRaiz = ["/", "/hoje", "/oportunidades", "/atestados"].includes(pathname);
  // O nome da seção em que a pessoa está, no botão do menu do celular.
  const secaoAtual = naLicitacao
    ? "Oportunidades"
    : pathname === "/"
      ? "Buscar"
      : pathname === "/hoje"
        ? "Hoje"
        : pathname === "/oportunidades"
          ? "Oportunidades"
          : pathname.startsWith("/atestados")
            ? "Atestados"
            : pathname === "/editais"
              ? "Enviar edital"
              : (CONFIGURACAO.find((r) => pathname === r.para || pathname.startsWith(r.para + "/"))
                  ?.rotulo ?? "Menu");

  // A BADGE É O NÚMERO DA ABA "Encontradas", e sai da MESMA conta que ela
  // (2026-09-18). Antes vinha de `/rodadas/{id}/achados`, que conta TUDO o que
  // a varredura avistou, sem os filtros do funil: a badge dizia 1.268 e a aba
  // abria 90. Dois números com o mesmo nome e fontes diferentes divergem —
  // é o mesmo defeito que as abas já tiveram quando liam a faceta errada.
  //
  // MESMA `queryKey` da tela de Oportunidades: o react-query serve as duas com
  // uma requisição só, e nenhuma delas pode mostrar número diferente da outra.
  //
  // O pipeline grava a cada licitação, não no fim, e `encontradas` é o que o
  // funil AINDA NÃO JULGOU — então o contador sobe enquanto a varredura corre,
  // desce enquanto a fila trabalha, e some quando ela termina. É o trabalho
  // pendente, visível de qualquer tela.
  const navega = useNavigate();
  const { data: rodando } = useQuery({
    queryKey: ["rodada-atual"],
    queryFn: api.rodadaAtual,
    refetchInterval: (q) => (q.state.data ? 3000 : 20000),
  });
  const emAndamento =
    !!rodando &&
    rodando.estado !== "concluida" &&
    rodando.estado !== "falhou" &&
    rodando.estado !== "cancelada";
  const { data: facetas } = useQuery({
    queryKey: ["facetas"],
    queryFn: api.facetas,
    refetchInterval: emAndamento ? 5000 : 60000,
  });
  const novos = facetas?.abas.encontradas ?? 0;

  // Tela nova começa do topo. POP (voltar/avançar) fica com o navegador, que
  // restaura a posição. Compara com o caminho anterior porque o tipo muda
  // sozinho: depois de um voltar, o primeiro filtro de Oportunidades
  // (setParams com replace) troca POP por REPLACE sem mudar de tela. Layout
  // effect para a tela nova não aparecer um quadro no meio antes de subir.
  const tipoNav = useNavigationType();
  const caminhoAnterior = useRef(pathname);
  useLayoutEffect(() => {
    if (caminhoAnterior.current === pathname) return;
    caminhoAnterior.current = pathname;
    if (tipoNav !== "POP") window.scrollTo(0, 0);
  }, [pathname, tipoNav]);

  // O MENU DO CELULAR (2026-09-28). Até 1024px a barra não comporta a
  // navegação — ela tem 589px de largura mínima e, sem caber, estourava a
  // tela para o lado, com "Configuração" e "Sair" fora dela. Abaixo disso a
  // barra fica com a marca e um botão, e os destinos vão para um painel.
  const [aberto, setAberto] = useState(false);
  const botaoMenu = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLElement>(null);
  const fechar = useCallback((devolverFoco: boolean) => {
    setAberto(false);
    if (devolverFoco) botaoMenu.current?.focus({ preventScroll: true });
  }, []);

  // Enquanto aberto: o foco entra no painel, a página não rola por baixo dele,
  // e o Esc fecha. Passar de 1024px (girar o tablet, alargar a janela) fecha
  // também — lá o painel não aparece, e ficaria aberto invisível com a
  // rolagem travada.
  useEffect(() => {
    if (!aberto) return;
    painel.current?.focus({ preventScroll: true });
    const raiz = document.documentElement;
    const antes = raiz.style.overflow;
    raiz.style.overflow = "hidden";
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") fechar(true);
    };
    const estreita = window.matchMedia("(max-width: 1024px)");
    const alargou = () => {
      if (!estreita.matches) fechar(false);
    };
    document.addEventListener("keydown", esc);
    estreita.addEventListener("change", alargou);
    return () => {
      raiz.style.overflow = antes;
      document.removeEventListener("keydown", esc);
      estreita.removeEventListener("change", alargou);
    };
  }, [aberto, fechar]);

  // O mergulho esconde a barra e o painel junto, mas a rolagem ficaria
  // travada. E a troca de tela que não passou pelo painel — o voltar do
  // navegador ou do Android — também fecha.
  useEffect(() => {
    if (mergulhando) fechar(false);
  }, [mergulhando, fechar]);
  useEffect(() => {
    fechar(false);
  }, [pathname, fechar]);

  // Foco que sai do menu fecha o menu: Tab depois do último item leva ao
  // conteúdo, Shift+Tab a partir do botão leva à marca — os dois atrás do
  // fundo, com o painel aberto por cima. Vai no botão E no painel, porque o
  // foco pode sair por qualquer um dos dois. Sem destino (toque no fundo, ou o
  // Safari, que não foca botão no clique) não decide nada: quem fecha é o
  // clique.
  const saiuDoMenu = (e: React.FocusEvent) => {
    const para = e.relatedTarget;
    if (para && !painel.current?.contains(para) && para !== botaoMenu.current) fechar(false);
  };

  // O VOLTAR DO CELULAR, em toda tela menos a inicial (pedido do Pedro,
  // 2026-10-02). Com passo anterior dentro do app, e o voltar do navegador —
  // a lista volta na mesma posicao. Sem ele (link direto), sobe para a
  // tela-mae em vez de sair do sistema. `idx` e o indice que o React Router
  // grava no `history.state` de cada entrada.
  const voltar = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navega(-1);
    else navega(telaMae(pathname), { replace: true });
  };

  return (
    <div className={css.tudo}>
      {!semVeu && <div className={css.recuo} aria-hidden="true" />}
      {naVarredura && <div className={css.fossa} aria-hidden="true" />}
      <a href="#conteudo" className={css.pular}>
        Pular para o conteúdo
      </a>

      <header className={css.barra} data-oculta={mergulhando}>
        {/* Coluna 1 da grade: a marca e, quando houver, o voltar ao lado dela.
            O voltar mora AQUI, e nao na tela de varredura, porque e a grade
            desta barra que alinha a navegacao — na primeira coluna ele nasce
            na mesma linha e com a mesma tipografia dos links, sem nenhum
            calculo de posicao para acertar a altura. */}
        <div className={css.esquerda}>
          {/* Só no celular (até 1024px, a mesma largura do menu suspenso): no
              desktop a navegação inteira está à vista, e o "Voltar" da
              varredura, logo abaixo, continua o mesmo. */}
          {!naRaiz && (
            <button
              type="button"
              className={css.voltarCelular}
              onClick={voltar}
              aria-label="Voltar"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M12.5 4 6.5 10l6 6" />
              </svg>
            </button>
          )}
          {/* Serve do arquivo estatico, e nao de um import: asset que falta
              vira 404 e a barra segue de pe. Um `import` de arquivo ausente
              derruba o build inteiro do Vite, e a marca nao vale isso. */}
          <Link to="/" className={css.marca} aria-label="NECTUNO — início">
            <img src="/nectuno.png" alt="NECTUNO" />
          </Link>
          {naVarredura && (
            <Link to="/" className={css.voltar}>
              Voltar
            </Link>
          )}
        </div>

        {/* O CELULAR (20/10): a navegação é UM MENU RECOLHÍVEL no meio do alto,
            entre a marca e o tema. Fechado, o botão diz ONDE A PESSOA ESTÁ (o
            nome da seção atual); aberto, desce o painel com todos os destinos.
            Só aparece até 1024px. SEM O NÚMERO DAS ENCONTRADAS no botão (pedido
            do Pedro, 20/10): ele só aparece na opção Oportunidades, dentro do
            painel. */}
        <button
          ref={botaoMenu}
          type="button"
          className={css.botaoMenu}
          aria-expanded={aberto}
          aria-controls="menu-celular"
          onClick={() => {
            if (aberto) fechar(true);
            else setAberto(true);
          }}
          onBlur={saiuDoMenu}
        >
          <span className={css.rotuloMenu}>{secaoAtual}</span>
          <svg className={css.seta} width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 4.5 6 8l3.5-3.5" />
          </svg>
        </button>
        {/* O PAINEL: todos os destinos. Fecha no clique de qualquer link,
            inclusive o da página atual e o das encontradas, que não mudam o
            pathname. */}
        <nav
          id="menu-celular"
          ref={painel}
          className={css.painel}
          aria-label="Principal"
          tabIndex={-1}
          // SEMPRE NO DOM (2026-10-02): com `hidden` ele aparecia e sumia de
          // uma vez, e o fade pedido precisa de um estado intermediario. Quem
          // tira o painel fechado do teclado e do leitor de tela e o `inert`
          // (e o `visibility` do CSS, depois do fade).
          data-aberto={aberto}
          inert={!aberto}
          onBlur={saiuDoMenu}
          onClick={(e) => {
            if ((e.target as Element).closest("a")) fechar(true);
          }}
        >
          <NavLink to="/" end className={ativoSe}>
            Buscar
          </NavLink>
          <NavLink
            to="/oportunidades"
            className={({ isActive }) => (isActive || naLicitacao ? css.ativo : "")}
          >
            Oportunidades
            {novos > 0 && (
              <span className={css.contador}>
                {novos}
                <span className="apenas-leitor">
                  {novos === 1 ? " encontrada" : " encontradas"} sem julgamento
                </span>
              </span>
            )}
          </NavLink>
          <NavLink to="/atestados" className={ativoSe}>
            Atestados
          </NavLink>
          <NavLink to="/editais" className={ativoSe}>
            Enviar edital
          </NavLink>
          <NavLink to="/configuracao" className={() => (naConfiguracao ? css.ativo : "")}>
            Configuração
          </NavLink>
          {DEV_ATIVO && (
            <NavLink
              to="/dev"
              className={({ isActive }) => `${css.dev} ${isActive ? css.ativo : ""}`}
            >
              Dev
            </NavLink>
          )}
          {/* A conta mora aqui: no celular a barra não tem lugar para o
              e-mail, e o papel só existia no `title`, que o toque não mostra. */}
          {/* A conta numa linha só, com o Sair ao lado (2026-10-02): eram duas
              linhas cheias no fim de um painel já comprido. */}
          {usuario && (
            <div className={css.contaPainel}>
              <p className={css.quemPainel}>
                {usuario.email} · {usuario.papel}
              </p>
              <button
                type="button"
                className={css.sairPainel}
                onClick={() => {
                  fechar(false);
                  void sair();
                }}
              >
                Sair
              </button>
            </div>
          )}
        </nav>
        {/* Toque fora fecha. Cobre a tela inteira, marca inclusive, abaixo do
            botão e do painel. */}
        {aberto && <div className={css.fundo} aria-hidden="true" onClick={() => fechar(false)} />}

        <nav aria-label="Principal">
          {/* "Hoje" saiu da navegação (20/10, pedido do Pedro): a tela e a rota
              /hoje continuam existindo, só não há mais link para ela na barra. */}
          <NavLink to="/" end className={({ isActive }) => (isActive ? css.ativo : "")}>
            Buscar
          </NavLink>
          {/* Também marcada dentro de uma oportunidade (/licitacao/N): a tela é
              um degrau dela, e sem isso a barra não dizia onde a pessoa está. */}
          <NavLink
            to="/oportunidades"
            className={({ isActive }) => (isActive || naLicitacao ? css.ativo : "")}
          >
            Oportunidades
            {novos > 0 && (
              // O NUMERO LEVA A ABA QUE ELE CONTA (2026-09-18). Ele levava a
              // /varredura porque a aba "Encontradas" cortava por faixa e nao
              // mostrava recem-encontrada nenhuma; agora a aba E as sem
              // julgamento, entao o numero e a lista sao a mesma coisa. Ela e
              // <span> dentro do <a> do NavLink — link dentro de link nao
              // vale —, entao o clique e interceptado.
              <span
                className={css.contador}
                role="link"
                tabIndex={0}
                title={`${novos} ${novos === 1 ? "licitação encontrada" : "licitações encontradas"} que o funil ainda não julgou. Clique para ver quais são.`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navega("/oportunidades?encontradas=true");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    navega("/oportunidades?encontradas=true");
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {novos}
              </span>
            )}
          </NavLink>

          {/* QUATRO SOLTOS E DOIS GRUPOS. Os soltos são o que se faz todo dia
              — e "Enviar edital" está aqui por ser uma AÇÃO, não uma tela de
              consulta: quem chega com um edital na mão não deve ter de abrir
              um menu para entregá-lo. */}
          <NavLink to="/editais" className={({ isActive }) => (isActive ? css.ativo : "")}>
            Enviar edital
          </NavLink>
          {/* ATESTADOS (2026-09-29) fica solto, e fora de Configuração: é
              consulta ao acervo que sustenta todo score, e não ajuste do
              sistema — o grupo espelha as seções da tela de configuração, e
              atestado não é uma delas. Na barra ele custa largura; ver
              `.barra nav` no CSS, que encolhe o vão para caber. */}
          <NavLink to="/atestados" className={ativoSe}>
            Atestados
          </NavLink>

          {/* UM LINK SÓ para a área de configuração: as cinco telas dela estão
              na coluna da esquerda de cada uma. Marcado em qualquer delas. */}
          <NavLink to="/configuracao" className={() => (naConfiguracao ? css.ativo : "")}>
            Configuração
          </NavLink>
          {/* Sai junto com `src/dev/`. Fica por ultimo e apagado de proposito:
              e uma area de implementacao, nao um destino do trabalho. */}
          {DEV_ATIVO && (
            <NavLink
              to="/dev"
              className={({ isActive }) => `${css.dev} ${isActive ? css.ativo : ""}`}
            >
              Dev
            </NavLink>
          )}
        </nav>

        <div className={css.direita}>
          {usuario && (
            <>
              <span className={css.quem} title={`Papel: ${usuario.papel}`}>
                {usuario.email}
              </span>
              <button type="button" onClick={() => void sair()} className={css.sair}>
                Sair
              </button>
            </>
          )}
          {!naVarredura && (<button
          type="button"
          className={css.tema}
          aria-label={claro ? "Mudar para o tema escuro" : "Mudar para o tema claro"}
          title={claro ? "Mudar para o tema escuro" : "Mudar para o tema claro"}
          onClick={() => defineTema(claro ? "escuro" : "claro")}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor"
               strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {claro ? (
              <path d="M13.5 9.6A5.7 5.7 0 0 1 6.4 2.5a5.7 5.7 0 1 0 7.1 7.1Z" />
            ) : (
              <>
                <circle cx="8" cy="8" r="2.8" />
                <path d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1" />
              </>
            )}
          </svg>
        </button>)}
        </div>
      </header>

      <main id="conteudo" className={css.conteudo}>
        <Outlet />
      </main>
    </div>
  );
}
